import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { useChurch } from '../../context/ChurchContext';
import { ChurchIcon } from '../common/Icons';
import api from '../../api/axios';
import usePermissions from '../../hooks/usePermissions';

const DJANGO_ADMIN_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api')
  .replace(/\/api\/?$/, '') + '/admin/';

/* ── SVG Icons ── */
const Ico = (d, size = 16) => () => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, display:'block' }}>
    {d}
  </svg>
);

const HomeNav        = Ico(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>);
const CalNav         = Ico(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>);
const CheckNav       = Ico(<><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>);
const UsersNav       = Ico(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>);
const ChartNav       = Ico(<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></>);
const PlusIco        = Ico(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>, 14);
const PersonIco      = Ico(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></>, 14);
const MembersIcon    = Ico(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></>);
const AdminIcon      = Ico(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>);
const SettingsIcon   = Ico(<><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M4.93 19.07l1.41-1.41M19.07 19.07l-1.41-1.41M12 2v2M12 20v2M2 12h2M20 12h2"/></>);
const HistoryIcon    = Ico(<><polyline points="12 8 12 12 14 14"/><path d="M3.05 11a9 9 0 1 1 .5 4m-.5-4v-4h4"/></>);
const DjangoIcon     = Ico(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>);
const GlobeIcon      = Ico(<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>);
const ChurchBldg     = Ico(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>);
const LandingIcon    = Ico(<><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>);
const AccountingIcon = Ico(<><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><line x1="7" y1="8" x2="7" y2="13"/><line x1="12" y1="6" x2="12" y2="13"/><line x1="17" y1="10" x2="17" y2="13"/></>);
const ImportIcon     = Ico(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>);
const CardIcon       = Ico(<><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></>);
const MailIcon       = Ico(<><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 6l-10 7L2 6"/></>);
const BellIcon       = Ico(<><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>);
const ShieldNav      = Ico(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>);
const QrIcon         = Ico(<><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="16" y="3" width="5" height="5" rx="1"/><rect x="3" y="16" width="5" height="5" rx="1"/><line x1="16" y1="16" x2="21" y2="21"/></>, 13);
const ChevronIco     = Ico(<polyline points="6 9 12 15 18 9"/>, 12);
const BoltIcon       = Ico(<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>);
const FinanceIcon    = Ico(<><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 6v2m0 8v2M9.5 9.5C9.5 8.12 10.62 7 12 7s2.5 1.12 2.5 2.5c0 1.38-1.12 2.5-2.5 2.5S9.5 11.88 9.5 10.5C9.5 12.12 10.62 13 12 13s2.5 1.12 2.5 2.5"/></>);
const PeopleIcon     = Ico(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>);
const EventIcon      = Ico(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="8" cy="15" r="1" fill="currentColor"/><circle cx="12" cy="15" r="1" fill="currentColor"/><circle cx="16" cy="15" r="1" fill="currentColor"/></>);

/* ── NavItem (item cliquable) ── */
function NavItemFull({ to, label, Icon, end = false, onClick, sub = false, badge }) {
  const [hov, setHov] = useState(false);
  return (
    <NavLink to={to} end={end} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={({ isActive }) => ({
        display:'flex', alignItems:'center', gap:'9px',
        padding: sub ? '6px 10px 6px 36px' : '8px 10px',
        margin:'1px 6px', borderRadius:'9px', textDecoration:'none',
        fontSize: sub ? '0.84rem' : '0.875rem',
        fontWeight: isActive ? 600 : 400,
        cursor:'pointer', transition:'all 0.15s',
        color: isActive ? '#fff' : hov ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.58)',
        background: isActive ? 'rgba(255,255,255,0.13)' : hov ? 'rgba(255,255,255,0.07)' : 'transparent',
        boxShadow: isActive ? 'inset 3px 0 0 rgba(255,255,255,0.5)' : 'none',
      })}>
      {sub
        ? <span style={{ width:5, height:5, borderRadius:'50%', background:'rgba(255,255,255,0.35)', flexShrink:0 }} />
        : <span style={{ width:18, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon /></span>
      }
      <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</span>
      {badge && (
        <span style={{ fontSize:'0.62rem', fontWeight:700, background:'rgba(255,255,255,0.18)', color:'rgba(255,255,255,0.8)', padding:'1px 6px', borderRadius:99, marginLeft:2 }}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}

/* ── ActionBtn ── */
function ActionBtn({ label, Icon, color, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', alignItems:'center', gap:'9px',
        width:'calc(100% - 12px)', margin:'1px 6px',
        padding:'7px 10px 7px 36px', borderRadius:'9px', border:'none',
        cursor:'pointer', fontSize:'0.835rem', fontWeight:500,
        color: hov ? '#fff' : color,
        background: hov ? `${color}28` : `${color}12`,
        transition:'all 0.15s', textAlign:'left', fontFamily:'inherit',
      }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }} />
      <span style={{ width:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color }}><Icon /></span>
      <span>{label}</span>
    </button>
  );
}

/* ── Section dépliable ── */
function NavSection({ label, Icon, iconColor, children, defaultOpen = true, paths = [] }) {
  const location = useLocation();
  const hasActive = paths.some(p => location.pathname.startsWith(p));
  const [open, setOpen] = useState(defaultOpen || hasActive);
  const [hov, setHov] = useState(false);

  return (
    <div style={{ margin:'1px 0' }}>
      <button onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          display:'flex', alignItems:'center', gap:'9px',
          width:'calc(100% - 12px)', margin:'0 6px',
          padding:'7px 8px', borderRadius:'9px', border:'none',
          cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
          background: hov ? 'rgba(255,255,255,0.05)' : 'transparent',
        }}>
        {/* Icône ronde colorée */}
        <span style={{
          width:26, height:26, borderRadius:7, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center',
          background: open
            ? (iconColor ? `${iconColor}28` : 'rgba(255,255,255,0.13)')
            : 'rgba(255,255,255,0.06)',
          color: open
            ? (iconColor || 'rgba(255,255,255,0.85)')
            : 'rgba(255,255,255,0.38)',
          transition:'all 0.2s',
        }}>
          <Icon />
        </span>
        <span style={{
          flex:1, textAlign:'left',
          fontSize:'0.71rem', fontWeight:700,
          textTransform:'uppercase', letterSpacing:'0.09em',
          color: open ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.35)',
          transition:'color 0.15s',
        }}>
          {label}
        </span>
        <span style={{
          transition:'transform 0.22s',
          transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          display:'flex', color:'rgba(255,255,255,0.28)',
        }}>
          <ChevronIco />
        </span>
      </button>

      <div style={{
        overflow:'hidden',
        maxHeight: open ? '600px' : '0px',
        opacity: open ? 1 : 0,
        transition:'max-height 0.28s ease, opacity 0.2s ease',
        paddingBottom: open ? 4 : 0,
      }}>
        {children}
      </div>
    </div>
  );
}

/* ── Séparateur ── */
function Divider() {
  return <div style={{ height:1, background:'rgba(255,255,255,0.07)', margin:'5px 14px' }} />;
}

/* ── Lien externe (dans section) ── */
function ExtSubLink({ href, label, Icon, newTab = true }) {
  const [hov, setHov] = useState(false);
  return (
    <a href={href} target={newTab ? '_blank' : '_self'} rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', alignItems:'center', gap:'9px',
        padding:'6px 10px 6px 40px', margin:'1px 6px', borderRadius:'9px',
        textDecoration:'none', fontSize:'0.835rem', fontWeight:400,
        color: hov ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
        background: hov ? 'rgba(255,255,255,0.07)' : 'transparent',
        transition:'all 0.15s',
      }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:'rgba(255,255,255,0.3)', flexShrink:0 }} />
      <span style={{ width:16, display:'flex', alignItems:'center', justifyContent:'center' }}><Icon /></span>
      <span style={{ flex:1 }}>{label}</span>
      {newTab && (
        <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity:0.35 }}>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      )}
    </a>
  );
}

/* ── Lien externe (bas sidebar) ── */
function ExtBottomLink({ href, label, Icon }) {
  const [hov, setHov] = useState(false);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', alignItems:'center', gap:'9px',
        padding:'6px 10px', margin:'1px 0', borderRadius:'9px',
        textDecoration:'none', fontSize:'0.81rem', fontWeight:400,
        color: hov ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.42)',
        background: hov ? 'rgba(255,255,255,0.07)' : 'transparent',
        transition:'all 0.15s',
      }}>
      <span style={{ width:16, display:'flex', alignItems:'center', justifyContent:'center' }}><Icon /></span>
      <span style={{ flex:1 }}>{label}</span>
      <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity:0.3 }}>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
    </a>
  );
}

/* ════════════════════════════════════════════
   SIDEBAR PRINCIPALE
════════════════════════════════════════════ */
export default function Sidebar({ isOpen, style = {} }) {
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const { isPlatformAdmin, selectedChurchId } = useChurch();
  const navigate = useNavigate();
  const isGlobalMode = isPlatformAdmin && selectedChurchId === null;
  const canCreate       = ['admin','superviseur','responsable','accueil','superadmin_platform'].includes(user?.role);
  const isSuperAdmin    = user?.role === 'superadmin_platform';

  const {
    isAdmin, isAdminOrResp,
    canSeeActivites, canSeeFideles, canSeeFinance, canSeeAdmin, canSeeNotifs, canSeeChurch,
  } = usePermissions();
  const canImport = isAdminOrResp || canSeeFideles || canSeeActivites;

  const [newContactCount, setNewContactCount] = useState(0);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchCount = () => {
      api.get('/notifications/contact/submissions/')
        .then(r => {
          const items = Array.isArray(r.data) ? r.data : [];
          setNewContactCount(items.filter(i => i.status === 'new').length);
        })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  return (
    <aside style={{
      width: isOpen ? '252px' : '0px',
      minWidth: isOpen ? '252px' : '0px',
      background: config.sidebarBg,
      display:'flex', flexDirection:'column',
      overflow:'hidden',
      transition:'width 0.25s ease, min-width 0.25s ease',
      flexShrink:0,
      boxShadow: isOpen ? '2px 0 16px rgba(0,0,0,0.2)' : 'none',
      ...style,
    }}>
      <div style={{ width:'252px', display:'flex', flexDirection:'column', height:'100%', overflowY:'auto', overflowX:'hidden' }}>

        {/* ── Logo app ── */}
        <div style={{ padding:'14px 12px 10px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 10px', borderRadius:11, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
              {!isGlobalMode && config.logoUrl
                ? <img src={config.logoUrl} alt="logo" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <ChurchIcon size={17} color="rgba(255,255,255,0.9)" />
              }
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:'0.83rem', fontWeight:700, color:'rgba(255,255,255,0.92)', lineHeight:1.25, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {isGlobalMode ? 'RCC' : (config.siteName || 'RCC')}
              </div>
              <div style={{ fontSize:'0.66rem', color:'rgba(255,255,255,0.35)', marginTop:1 }}>
                {isGlobalMode ? 'Vue globale · toutes les églises' : "Gestion d'église"}
              </div>
            </div>
          </div>
        </div>

        {/* ── Chef de Famille : un seul écran (RCC-RAP-001) ── */}
        {user?.role === 'chef_famille' ? (
          <div style={{ padding:'0 0 2px' }}>
            <NavItemFull to="/ma-famille" label="Ma famille" Icon={PeopleIcon} end />
          </div>
        ) : (
        <>
        {/* ── Tableau de bord (item racine) ── */}
        <div style={{ padding:'0 0 2px' }}>
          <NavItemFull to="/dashboard" label="Tableau de bord" Icon={HomeNav} end />
        </div>

        <Divider />

        {/* Organisation RCC-NAV-001 (§11.2) : A Pilotage & activités · B Fidèles &
            participants · C Finances · D Administration · E Ministères (phase ultérieure) */}

        {/* ════ A. PILOTAGE & ACTIVITÉS ════ */}
        {canSeeActivites && (
          <NavSection
            label="Pilotage & activités"
            Icon={EventIcon}
            iconColor="#7CB89A"
            defaultOpen={true}
            paths={['/activites', '/presences', '/statistiques', '/galerie']}
          >
            <NavItemFull to="/activites"   label="Événements"   Icon={CalNav}   sub />
            <NavItemFull to="/presences"   label="Présences"    Icon={CheckNav} sub />
            <NavItemFull to="/statistiques" label="Statistiques" Icon={ChartNav} sub />
            {isAdmin && <NavItemFull to="/galerie" label="Galerie" Icon={LandingIcon} sub />}
          </NavSection>
        )}

        {/* ════ B. FIDÈLES & PARTICIPANTS ════ */}
        {canSeeFideles && (
          <NavSection
            label="Fidèles & participants"
            Icon={PeopleIcon}
            iconColor="#88BBFF"
            defaultOpen={true}
            paths={['/fideles', '/participants', '/adhesions', '/familles', '/rapports-familles', '/doublons']}
          >
            <NavItemFull to="/fideles"      label="Fidèles (membres)"    Icon={MembersIcon} sub />
            <NavItemFull to="/participants" label="Tous les participants" Icon={UsersNav}    sub />
            {(isAdminOrResp || canSeeFideles) && <NavItemFull to="/adhesions" label="Demandes d'adhésion" Icon={AdminIcon} sub />}
            {(isAdminOrResp || canSeeFideles) && <NavItemFull to="/familles" label="Familles" Icon={PeopleIcon} sub />}
            {isAdminOrResp && <NavItemFull to="/rapports-familles" label="Rapports Familles" Icon={ChartNav} sub />}
            {isAdmin && <NavItemFull to="/doublons" label="Doublons" Icon={PeopleIcon} sub />}
            {canCreate && (
              <ActionBtn label="Ajouter un participant" Icon={PersonIco} color="#88BBFF"
                onClick={() => { sessionStorage.setItem('openCreateParticipant','1'); navigate('/participants'); }} />
            )}
          </NavSection>
        )}

        {/* ════ C. FINANCES ════ */}
        {canSeeFinance && (
          <NavSection
            label="Finances"
            Icon={FinanceIcon}
            iconColor="#F59E0B"
            defaultOpen={false}
            paths={['/comptabilite']}
          >
            <NavItemFull to="/comptabilite"  label="Comptabilité"  Icon={AccountingIcon} sub />
          </NavSection>
        )}

        {/* ════ D. ADMINISTRATION ════ */}
        {/* La gestion des utilisateurs/droits reste toujours réservée au rôle
            admin strict (jamais accordable via un groupe) — sinon un compte
            "Consultation" pourrait s'auto-élever en s'ajoutant des droits. */}
        {(isAdminOrResp || canSeeAdmin || canSeeNotifs || canSeeChurch) && (
          <NavSection
            label="Administration"
            Icon={AdminIcon}
            iconColor="#A78BFA"
            defaultOpen={false}
            paths={['/mon-eglise', '/administration', '/import', '/permissions', '/cartes', '/historique', '/parametres']}
          >
            {(isAdmin || canSeeChurch) && <NavItemFull to="/mon-eglise"     label="Mon église"          Icon={ChurchBldg}   sub />}
            {(isAdmin || canSeeAdmin) && <NavItemFull to="/administration" label="Utilisateurs"         Icon={AdminIcon}    sub />}
            {isAdmin && <NavItemFull to="/permissions"    label="Droits & groupes"     Icon={ShieldNav}    sub />}
            {canImport && <NavItemFull to="/import"   label="Import Excel / CSV"  Icon={ImportIcon}   sub />}
            {isAdmin && <NavItemFull to="/cartes"         label="Cartes de membre"    Icon={CardIcon}     sub />}
            {(isAdmin || canSeeNotifs) && <NavItemFull to="/historique"  label="Journal d'activité" Icon={HistoryIcon}  sub />}
            {(isAdmin || canSeeChurch) && <NavItemFull to="/parametres"  label="Paramètres du site" Icon={SettingsIcon} sub />}
          </NavSection>
        )}

        {/* ════ COMMUNICATION ════ */}
        {(isAdmin || canSeeNotifs) && (
          <NavSection
            label="Communication"
            Icon={BellIcon}
            iconColor="#F472B6"
            defaultOpen={false}
            paths={['/notifications-push', '/notifications-whatsapp']}
          >
            <NavItemFull to="/notifications-push"      label="Notifications push"     Icon={BellIcon} sub />
            <NavItemFull to="/notifications-whatsapp"  label="Notifications WhatsApp" Icon={BellIcon} sub />
          </NavSection>
        )}

        {/* ════ PLATEFORME (super admin) ════ */}
        {isPlatformAdmin && (
          <NavSection
            label="Plateforme"
            Icon={GlobeIcon}
            iconColor="#34D399"
            defaultOpen={false}
            paths={['/super-admin', '/landing-settings', '/demandes-contact']}
          >
            <NavItemFull to="/super-admin"      label="Gestion des églises" Icon={GlobeIcon}   sub />
            <NavItemFull to="/landing-settings" label="Page d'accueil"      Icon={LandingIcon} sub />
            <NavItemFull to="/demandes-contact" label="Demandes de contact" Icon={MailIcon}    sub badge={newContactCount > 0 ? newContactCount : null} />
            <ExtSubLink href={DJANGO_ADMIN_URL} label="Django Admin" Icon={DjangoIcon} />
          </NavSection>
        )}

        {/* ════ 6. ACTIONS RAPIDES ════ */}
        {canCreate && (
          <>
            <Divider />
            <NavSection label="Actions rapides" Icon={BoltIcon} iconColor="#FBBF24" defaultOpen={true} paths={[]}>
              <ActionBtn label="Créer un événement" Icon={PlusIco} color="#7CB89A"
                onClick={() => { sessionStorage.setItem('openCreateActivity','1'); navigate('/activites'); }} />
            </NavSection>
          </>
        )}
        </>
        )}

        <div style={{ flex:1 }} />

        {/* ════ Accès externes (bas) ════ */}
        <Divider />
        <div style={{ padding:'4px 6px 8px' }}>
          <div style={{ fontSize:'0.64rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', padding:'3px 10px 5px', color:'rgba(255,255,255,0.25)' }}>
            Accès externes
          </div>
          <ExtBottomLink href="/accueil-mobile" label="Accueil Entrée" Icon={QrIcon} />
          <ExtBottomLink href="/fidele" label="Portail Fidèle" Icon={() => <ChurchIcon size={13} color="currentColor" />} />
        </div>

        {/* Version */}
        <div style={{ padding:'4px 16px 14px' }}>
          <span style={{ fontSize:'0.63rem', color:'rgba(255,255,255,0.15)' }}>v1.0.0 · RCC</span>
        </div>

      </div>
    </aside>
  );
}
